// FirstTimeHero — radically simplified cold open with an A/B-tested
// framing. Two variants split 50/50 (sticky per visitor):
//
//   • "pot"     — leads with the live hackathon prize.
//   • "promise" — leads with the 60-second first-win promise.
//
// Variant assignment + view/click events are tracked via funnelTracker
// so we can compare conversion. `?hv=pot|promise` forces a variant.
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { Trophy, Zap, ChevronRight, Clock } from "lucide-react";
import { useBeginnerMode } from "@/hooks/useBeginnerMode";
import { useHackathon, formatUsd } from "@/hooks/useHackathon";
import {
  getOrAssignHeroVariant,
  trackHeroVariantClick,
  trackHeroVariantView,
  type HeroVariant,
} from "@/lib/heroVariant";

function useCountUp(target: number, durationMs = 900) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return val;
}

const FirstTimeHero = () => {
  const { start } = useBeginnerMode();
  const { pot } = useHackathon({ scoreboardLimit: 1 });
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref")?.trim() || null;
  const [showFullSite, setShowFullSite] = useState(false);
  const [variant] = useState<HeroVariant>(() => getOrAssignHeroVariant());

  // Fire one view event per mount.
  useEffect(() => {
    trackHeroVariantView(variant);
  }, [variant]);

  const potCents = pot?.pot_cents ?? 1042700;
  const animatedCents = useCountUp(potCents);
  const potLabel = formatUsd(animatedCents);

  const handleStart = () => {
    trackHeroVariantClick(variant);
    start(1);
  };

  if (showFullSite) return null;

  // Variant-aware copy. Friend referrals override headline regardless
  // of the variant since the personal hook is stronger than either.
  const isPot = variant === "pot";
  const eyebrowIcon = isPot ? Trophy : Clock;
  const eyebrowLabel = isPot ? "Live pot" : "First win in";
  const eyebrowValue = isPot ? potLabel : "60 sec";

  const headline = ref
    ? null
    : isPot
      ? (
          <>
            Win a slice of <span className="text-primary">{potLabel}</span>.
          </>
        )
      : (
          <>
            Your first AI win — <span className="text-primary">in 60 sec.</span>
          </>
        );

  const subhead = ref
    ? "Catch up — skill #1 unlocks the game."
    : isPot
      ? "100 tiny AI skills. The first one unlocks the game. ~60 seconds."
      : "100 tiny AI skills. We'll show you exactly what to type — and there's a real cash pot waiting once you're in.";

  const ctaLabel = isPot ? "Start playing — 60 sec" : "Get my first win";

  return (
    <>
      <Helmet>
        <title>
          {isPot
            ? "HackerLabs.ai — Win a slice of the pot. Start with skill #1."
            : "HackerLabs.ai — Your first AI win, in 60 seconds."}
        </title>
        <meta
          name="description"
          content={
            isPot
              ? "100 small AI skills. Win a real cash pot. No signup. Start your first AI win in under a minute."
              : "100 small AI skills, one at a time. No signup. We'll show you exactly what to type. Start your first AI win in under a minute."
          }
        />
      </Helmet>

      <section
        className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-10"
        aria-label="Welcome to HackerLabs"
        data-hero-variant={variant}
      >
        <div className="max-w-xl w-full text-center">
          <div className="inline-flex items-center gap-2 mb-6 rounded-full border border-primary/40 bg-primary/15 px-3.5 py-1.5 shadow-lg shadow-primary/10">
            {(() => {
              const I = eyebrowIcon;
              return <I className="w-3.5 h-3.5 text-primary animate-pulse" />;
            })()}
            <span className="text-[10px] font-mono uppercase tracking-wider text-primary">
              {eyebrowLabel}
            </span>
            <span className="text-xs font-mono font-bold text-foreground tabular-nums">
              {eyebrowValue}
            </span>
          </div>

          <h1 className="font-mono font-extrabold text-4xl sm:text-6xl text-foreground mb-3 leading-[1.05]">
            {ref ? (
              <>
                <span className="text-primary">{ref}</span>
                <br />
                challenged you.
              </>
            ) : (
              headline
            )}
          </h1>

          <p className="text-sm sm:text-base font-mono text-muted-foreground mb-8 leading-relaxed">
            {subhead}
          </p>

          <button
            type="button"
            onClick={handleStart}
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-xl bg-primary px-8 py-4 text-base sm:text-lg font-mono font-bold text-primary-foreground hover:bg-primary/90 shadow-xl shadow-primary/30 transition-all hover:shadow-primary/50 hover:scale-[1.02]"
          >
            <Zap className="w-5 h-5" />
            {ctaLabel}
            <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
          </button>

          <button
            type="button"
            onClick={() => setShowFullSite(true)}
            className="mt-12 text-[11px] font-mono text-muted-foreground/80 hover:text-primary underline-offset-4 hover:underline"
          >
            I already know AI — show me the full site
          </button>
        </div>
      </section>
    </>
  );
};

export default FirstTimeHero;
